import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { createFrameTask } from '@theme/utilities';

class CartDrawerComponent extends Component {

  #summaryThreshold = 0.5;
  #connection = new AbortController();
  #drawerOwner = null;
  #stickyFrame = createFrameTask(() => {
    if (this.isConnected) this.#updateStickyState();
  });

  get #themeDrawer() {
    return   (this.closest('theme-drawer'));
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#connection = new AbortController();
    this.#drawerOwner = this.#themeDrawer;
    this.addEventListener('click', this.#handleInstallmentsClick, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connection.abort();
    this.#stickyFrame.cancel();
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#drawerOwner?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#drawerOwner = null;
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  #handleCartSectionRestored = () => {
    this.#stickyFrame.schedule();
  };

  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const index = path.findIndex((node) => node instanceof Element && node.id === 'shopify-installments-cta');
    if (index < 0) return;
    const root = path.slice(index + 1).find((node) => node instanceof ShadowRoot);
    if (root instanceof ShadowRoot && root.host.matches('shopify-payment-terms')) this.#themeDrawer?.close();
  };

  #handleCartLinesUpdate = (event) => {
    const signal = this.#connection.signal;
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    const sourceModal =   (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        if (signal.aborted || !this.isConnected) return;
        const settle = () => this.#stickyFrame.schedule();

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (signal.aborted || !this.isConnected) return;
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true, signal });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {

      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
